import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  ChevronRight, ChevronDown, ChevronUp, Plus, Copy, Trash2, Search,
  FileText, Scale, BookOpen, Euro, UserCog, GraduationCap, CheckSquare,
  Settings, Link2, MailIcon, ListChecks, Bell, Layers, Layout as LayoutIcon,
  Users, Info, Building, Wallet, ClipboardCheck, Star
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_ICON_MAP = {
  'scale': Scale, 'book-open': BookOpen, 'file-spreadsheet': FileText,
  'euro': Euro, 'user-cog': UserCog, 'graduation-cap': GraduationCap,
  'check-square': CheckSquare,
};

const EMAIL_ICON_MAP = {
  'building': Building, 'euro': Euro, 'user-cog': UserCog,
  'graduation-cap': GraduationCap, 'wallet': Wallet,
  'clipboard-check': ClipboardCheck,
};

const CONFIG_MENU = [
  { id: 'comptes-acces', label: "Comptes d'acces", icon: Users, href: '/configuration/comptes-acces' },
  { id: 'modeles-documents', label: 'Modeles de documents', icon: FileText, href: '/configuration/modeles-documents' },
  { id: 'modeles-emails', label: "Modeles d'emails", icon: MailIcon, href: '/configuration/modeles-emails' },
  { id: 'automatisations', label: 'Automatisations', icon: Settings, href: '#' },
  { id: 'interconnexions', label: 'Interconnexions', icon: Link2, href: '#' },
  { id: 'modeles-taches', label: 'Modeles de listes de taches', icon: ListChecks, href: '#' },
  { id: 'notifications', label: 'Notifications', icon: Bell, href: '#' },
  { id: 'competences', label: 'Domaines de competences', icon: Layers, href: '#' },
  { id: 'marque-catalogue', label: 'Ma marque - Catalogue en ligne', icon: BookOpen, href: '#' },
  { id: 'espace-apprenant', label: 'Espace Apprenant', icon: LayoutIcon, href: '#' },
];

const ModelesDocumentsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEmailMode = location.pathname.includes('modeles-emails');

  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedCats, setExpandedCats] = useState({});

  const activeMenuId = isEmailMode ? 'modeles-emails' : 'modeles-documents';
  const pageTitle = isEmailMode ? "Modeles d'e-mails" : 'Modeles de documents';
  const basePath = isEmailMode ? '/configuration/modeles-emails' : '/configuration/modeles-documents';

  useEffect(() => { fetchData(); }, [isEmailMode]);

  const fetchData = async () => {
    try {
      const catEndpoint = isEmailMode ? 'email-categories' : 'categories';
      const tplEndpoint = isEmailMode ? 'emails' : 'documents';
      const [catRes, tplRes] = await Promise.all([
        fetch(`${API}/config/modeles/${catEndpoint}`, { credentials: 'include' }),
        fetch(`${API}/config/modeles/${tplEndpoint}`, { credentials: 'include' }),
      ]);
      if (catRes.ok) {
        const cats = await catRes.json();
        setCategories(cats);
        // First category expanded by default, rest collapsed
        const expanded = {};
        cats.forEach((c, i) => { expanded[c.key] = i === 0; });
        setExpandedCats(expanded);
      }
      if (tplRes.ok) setTemplates(await tplRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const getTemplatesForSubCat = (catKey, subKey) => {
    return templates.filter(t => t.category === catKey && t.sub_category === subKey);
  };

  const handleAddTemplate = async (catKey, subKey) => {
    try {
      const res = await fetch(`${API}/config/modeles/documents`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: 'Nouveau modele', category: catKey, sub_category: subKey, type: isEmailMode ? 'email' : 'document' })
      });
      if (res.ok) {
        const tpl = await res.json();
        navigate(`${basePath}/${tpl.template_id}`);
      }
    } catch (e) { toast.error("Erreur de creation"); }
  };

  const handleDuplicate = async (e, templateId) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API}/config/modeles/documents/${templateId}/duplicate`, {
        method: 'POST', credentials: 'include'
      });
      if (res.ok) { toast.success("Modele duplique"); fetchData(); }
    } catch (e) { toast.error("Erreur"); }
  };

  const handleSetDefault = async (e, templateId) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API}/config/modeles/documents/${templateId}/default`, {
        method: 'PUT', credentials: 'include'
      });
      if (res.ok) { toast.success("Defini par defaut"); fetchData(); }
    } catch (e) { toast.error("Erreur"); }
  };

  const handleDelete = async (e, templateId) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer ce modele ?")) return;
    try {
      await fetch(`${API}/config/modeles/documents/${templateId}`, { method: 'DELETE', credentials: 'include' });
      toast.success("Modele supprime"); fetchData();
    } catch (e) { toast.error("Erreur"); }
  };

  const filteredTemplates = search
    ? templates.filter(t => t.name.toLowerCase().includes(search.toLowerCase()))
    : templates;

  const iconMap = isEmailMode ? EMAIL_ICON_MAP : DOC_ICON_MAP;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin h-8 w-8 border-4 border-violet-500 border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex gap-8" data-testid="modeles-page">
        {/* Left config sidebar */}
        <div className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-24">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 px-3">Configuration</h3>
            <nav className="space-y-0.5">
              {CONFIG_MENU.map((item) => {
                const Icon = item.icon;
                const isActive = item.id === activeMenuId;
                return (
                  <button key={item.id}
                    onClick={() => item.href !== '#' ? navigate(item.href) : null}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                      isActive ? 'bg-violet-50 text-violet-700 font-semibold' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                    }`} data-testid={`config-menu-${item.id}`}>
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <span>Configuration</span>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-slate-600 font-medium">{pageTitle}</span>
          </div>

          <h1 className="text-3xl font-black text-slate-900" data-testid="page-title">{pageTitle}</h1>

          {/* Search + Signature */}
          <div className="flex items-center gap-3">
            <div className="relative max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Rechercher" value={search} onChange={e => setSearch(e.target.value)}
                className="pl-10" data-testid="search-input" />
            </div>
            {isEmailMode && (
              <Button className="bg-violet-700 hover:bg-violet-800 text-white gap-2" data-testid="signature-btn">
                <Star className="h-4 w-4" /> Signature electronique
              </Button>
            )}
          </div>

          {/* Search results mode */}
          {search && (
            <Card className="border shadow-sm">
              <CardContent className="py-4">
                <p className="text-sm text-slate-500 mb-3">{filteredTemplates.length} resultat(s)</p>
                {filteredTemplates.map(tpl => (
                  <TemplateRow key={tpl.template_id} template={tpl} navigate={navigate}
                    onDuplicate={handleDuplicate} onSetDefault={handleSetDefault} onDelete={handleDelete}
                    basePath={basePath} />
                ))}
              </CardContent>
            </Card>
          )}

          {/* Categories */}
          {!search && categories.map(cat => {
            const Icon = iconMap[cat.icon] || FileText;
            const isExpanded = expandedCats[cat.key];
            return (
              <Card key={cat.key} className="border shadow-sm" data-testid={`category-${cat.key}`}>
                <CardContent className="p-0">
                  <button onClick={() => setExpandedCats(p => ({ ...p, [cat.key]: !p[cat.key] }))}
                    className="w-full flex items-center justify-between px-6 py-4 group">
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5 text-violet-500" />
                      <h2 className="text-base font-bold text-slate-800">{cat.label}</h2>
                    </div>
                    {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                  </button>

                  {isExpanded && (
                    <div className="border-t border-slate-100">
                      {cat.info && (
                        <div className="px-6 py-3 bg-blue-50/50 flex items-start gap-2">
                          <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                          <p className="text-xs text-blue-700">{cat.info}</p>
                        </div>
                      )}
                      {cat.sub_categories.map(sub => {
                        const subTemplates = getTemplatesForSubCat(cat.key, sub.key);
                        return (
                          <div key={sub.key} className="border-t border-slate-50 px-6 py-4" data-testid={`subcat-${sub.key}`}>
                            <h4 className="text-sm font-semibold text-slate-700 mb-2">{sub.label}</h4>
                            {subTemplates.map(tpl => (
                              <TemplateRow key={tpl.template_id} template={tpl} navigate={navigate}
                                onDuplicate={handleDuplicate} onSetDefault={handleSetDefault} onDelete={handleDelete}
                                basePath={basePath} />
                            ))}
                            <button onClick={() => handleAddTemplate(cat.key, sub.key)}
                              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-violet-600 mt-2 font-medium border border-dashed border-slate-300 rounded-md px-3 py-1.5 hover:border-violet-400 transition-colors"
                              data-testid={`add-template-${sub.key}`}>
                              <Plus className="h-3.5 w-3.5" /> Ajouter un modele
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </Layout>
  );
};

const TemplateRow = ({ template, navigate, onDuplicate, onSetDefault, onDelete, basePath }) => (
  <div className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-slate-50 group transition-colors bg-slate-50/50 mb-1"
    data-testid={`template-row-${template.template_id}`}>
    <button onClick={() => navigate(`${basePath}/${template.template_id}`)}
      className="text-sm text-violet-600 hover:text-violet-800 hover:underline font-medium truncate text-left"
      data-testid={`template-link-${template.template_id}`}>
      {template.name}
    </button>
    <div className="flex items-center gap-1.5 shrink-0 ml-4">
      <button className="text-xs text-slate-500 hover:text-violet-600 px-2 py-1 rounded transition-colors"
        onClick={(e) => onDuplicate(e, template.template_id)} data-testid={`dup-${template.template_id}`}>
        Dupliquer
      </button>
      <button
        className={`text-xs px-2.5 py-1 rounded transition-colors ${
          template.is_default
            ? 'bg-violet-100 text-violet-700 font-medium'
            : 'text-slate-500 hover:text-violet-600 hover:bg-violet-50'
        }`}
        onClick={(e) => onSetDefault(e, template.template_id)}
        data-testid={`default-${template.template_id}`}>
        Par defaut
      </button>
      <button className="text-slate-400 hover:text-red-500 p-1 rounded transition-colors"
        onClick={(e) => onDelete(e, template.template_id)} data-testid={`delete-${template.template_id}`}>
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  </div>
);

export default ModelesDocumentsPage;
